import {
  AFFILIATE_CLICK_COOLDOWN_DAYS,
  AFFILIATE_DISMISS_DAYS,
  AFFILIATE_INTERSTITIAL_COOLDOWN_HOURS,
  AFFILIATE_MAX_IMPRESSIONS_PER_DAY,
  LOCAL_AFFILIATE_STATE_KEY,
} from "../constant/constant";
import { AffiliatePlacement } from "../data/affiliatePlacements";

interface AffiliateState {
  v: 1;
  /** "YYYY-MM-DD" — dùng để reset bộ đếm impression mỗi ngày. */
  day: string;
  impressionsToday: number;
  /** placement -> epoch ms lúc người dùng bấm ẩn. */
  dismissedAt: Record<string, number>;
  /** productId -> epoch ms lúc click gần nhất. */
  clickedAt: Record<string, number>;
  /** epoch ms lúc interstitial bắn gần nhất. */
  interstitialAt?: number;
}

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

const today = (): string => new Date().toISOString().slice(0, 10);

const emptyState = (): AffiliateState => ({
  v: 1,
  day: today(),
  impressionsToday: 0,
  dismissedAt: {},
  clickedAt: {},
});

/**
 * Mọi truy cập localStorage đều bọc try/catch: Safari private mode và trạng thái
 * hết quota đều throw. Khi hỏng thì suy biến thành "cứ hiện, không cap" — thà
 * hiện thừa còn hơn làm vỡ trang.
 */
const readState = (): AffiliateState => {
  try {
    const raw = localStorage.getItem(LOCAL_AFFILIATE_STATE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as AffiliateState;
    if (!parsed || parsed.v !== 1) return emptyState();
    // Sang ngày mới thì reset bộ đếm, giữ nguyên dismiss và lịch sử click.
    if (parsed.day !== today()) {
      return { ...parsed, day: today(), impressionsToday: 0 };
    }
    return {
      ...emptyState(),
      ...parsed,
      dismissedAt: parsed.dismissedAt ?? {},
      clickedAt: parsed.clickedAt ?? {},
    };
  } catch (error) {
    return emptyState();
  }
};

const writeState = (state: AffiliateState): void => {
  try {
    localStorage.setItem(LOCAL_AFFILIATE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    // Không làm gì — mất frequency cap vẫn tốt hơn là ném lỗi ra UI.
  }
};

/** Trang /do-dung-sinh-vien do người dùng chủ động vào nên không bị cap. */
const isAlwaysAllowed = (placement: AffiliatePlacement): boolean =>
  placement === "shop-page";

export const shouldShowAffiliate = (placement: AffiliatePlacement): boolean => {
  if (isAlwaysAllowed(placement)) return true;

  const state = readState();
  const dismissedAt = state.dismissedAt[placement];
  if (dismissedAt && Date.now() - dismissedAt < AFFILIATE_DISMISS_DAYS * DAY_MS) {
    return false;
  }
  return state.impressionsToday < AFFILIATE_MAX_IMPRESSIONS_PER_DAY;
};

export const recordAffiliateImpression = (
  placement: AffiliatePlacement
): void => {
  if (isAlwaysAllowed(placement)) return;
  const state = readState();
  writeState({ ...state, impressionsToday: state.impressionsToday + 1 });
};

export const recordAffiliateClick = (productId: string): void => {
  const state = readState();
  writeState({
    ...state,
    clickedAt: { ...state.clickedAt, [productId]: Date.now() },
  });
};

export const dismissAffiliate = (placement: AffiliatePlacement): void => {
  const state = readState();
  writeState({
    ...state,
    dismissedAt: { ...state.dismissedAt, [placement]: Date.now() },
  });
};

/** Sản phẩm đã click gần đây thì thôi gợi ý lại — họ xem rồi. */
export const getRecentlyClickedIds = (): string[] => {
  const state = readState();
  const cutoff = Date.now() - AFFILIATE_CLICK_COOLDOWN_DAYS * DAY_MS;
  return Object.keys(state.clickedAt).filter(
    (productId) => state.clickedAt[productId] > cutoff
  );
};

export const canShowInterstitial = (): boolean => {
  const state = readState();
  if (!state.interstitialAt) return true;
  return (
    Date.now() - state.interstitialAt >=
    AFFILIATE_INTERSTITIAL_COOLDOWN_HOURS * HOUR_MS
  );
};

export const recordInterstitialShown = (): void => {
  const state = readState();
  writeState({ ...state, interstitialAt: Date.now() });
};
